import { autoinject, transient } from "aurelia-dependency-injection";
import { bindable } from "aurelia-templating";

//Load styles
require("./comp-section-expander.scss");

@transient()
@autoinject()
export class CompSectionExpander {

    //Main title
    @bindable
    public mainTitle: string;

    //Subtitle
    @bindable
    public subTitle: string;

    //Whether the expander should be collapsed initially
    @bindable
    public collapsedInitially: boolean;

    //Whether the content of the expander has items using col-gutter or not
    @bindable
    public contentHasGutter: boolean;

    //If the expander is collapsed or not
    @bindable
    public expanderCollapsed: boolean;

    //If the expander should have animation or not (due to performance issues when multiple expanders expand/collapse simultaneously)
    @bindable
    public hasAnimation: boolean = true;

    //Is the content section being currently resized
    public isSectionResizing: boolean;

    //Hold value of the content section's max-height value
    private sectionContentMaxHeight: number = 0;

    //The count (pixels) by which the content will collapse/expand for each interval
    private expansionIncrementCount: number = 4;

    //The pace (interval timeout) by which the content will collapse/expand
    private expansionIncrementPace: number = 0.75; //Milliseconds

    /* The increment that is added to the 'expansionIncrementCountMultiplier' within the expand/collapse methods
       This 'expansionIncrementCountMultiplier' multiplied by the 'expansionIncrementCount' allows the content section to
       collapse/expand in a non-linear/exponential way.
       Increasing this value will speed up the collapse/expansion transitions exponentially, and vice-versa */
    private expansionMultiplierIncrement = 0.025;

    //Holds reference to interval id (setInterval) that expands/shrinks the expander
    private expansionIntervalId;

    //Reference to the content section within the section expander
    private sectionExpanderContentRef: HTMLElement;

    private _isAttached: boolean = false;

    constructor(private element: Element) { }

    attached() {
        //Set initial 'collapsed' state
        if (this.collapsedInitially) {
            this.expanderCollapsed = true;
            this.setExpanderContentMaxHeight(0);
        }

        this._isAttached = true;
    }

    detached() {
        this.clearExpansionInterval();
    }

    /**
     * Set the max-height of the expander's content section
     * @param maxHeight Max height
     */
    setExpanderContentMaxHeight = (maxHeight: number) => {
        this.sectionContentMaxHeight = maxHeight;
        if (this.sectionExpanderContentRef) {
            this.sectionExpanderContentRef.style.maxHeight = `${this.sectionContentMaxHeight}px`;
        }
    }

    /**
     * Increase the content section's max-height by a value
     * @param increaseByValue Increase by value
     */
    increaseExpanderContentMaxHeight = (increaseByValue: number) => { this.setExpanderContentMaxHeight(this.sectionContentMaxHeight + increaseByValue); }

    /**
     * Decrease the content section's max-height by a value
     * @param increaseByValue Decrease by value
     */
    decreaseExpanderContentMaxHeight = (decreaseByValue: number) => { this.setExpanderContentMaxHeight(this.sectionContentMaxHeight - decreaseByValue); }

    /**
     * Expand the content transitionally (the height of the expander content increases exponentially until it fits all the content)
     */
    expandSectionContentIncrementally = () => {
        if (this.hasAnimation) {
            //The 'latestSectionContentHeight' is used to keep track of the latest height of the content section
            let latestSectionContentHeight = this.getSectionContentHeight();
            let expansionIncrementCountMultiplier = 1;

            //Set the component's content section's maximum height to its current height
            this.setExpanderContentMaxHeight(this.getSectionContentHeight());
            //Start increasing the content section's height
            this.increaseExpanderContentMaxHeight(this.expansionIncrementCount);

            this.clearExpansionInterval();

            this.expansionIntervalId = setInterval(() => {
                //If the content's height remained the same after increasing the height in the previous interval, stop increasing height
                if (this.getSectionContentHeight() === latestSectionContentHeight) {
                    this.setExpanderContentMaxHeight(latestSectionContentHeight);
                    this.stopContentResizing();
                    //Once the transition is complete, the content height needs to be able to stretch dynamically i.e. when the window is narrowed/widened
                    this.sectionExpanderContentRef.style.maxHeight = 'none';
                    return;
                }

                //Update the 'latestSectionContentHeight' variable before increasing the allowed max-height
                latestSectionContentHeight = this.getSectionContentHeight();

                //Increment the content section's allowed max-height
                this.increaseExpanderContentMaxHeight(this.expansionIncrementCount * expansionIncrementCountMultiplier);

                //Increase the height increment multiplier
                expansionIncrementCountMultiplier += this.expansionMultiplierIncrement;
            }, this.expansionIncrementPace);
        }
        else {
            this.isSectionResizing = false;
            this.sectionExpanderContentRef.style.maxHeight = 'none';
        }
    }
    /**
     * Collapse the content transitionally (the height of the expander content decreases exponentially until it reaches 0)
     */
    collapseSectionContentIncrementally = () => {
        if (this.hasAnimation) {
            let expansionIncrementCountMultiplier = 1;

            //Set the component's content section's maximum height to its current height
            this.setExpanderContentMaxHeight(this.getSectionContentHeight());
            //Decrease the maximum height of the content section. Decrease either by an increment value, or by the remaining content section's height
            const decreaseByValue = this.expansionIncrementCount < this.sectionContentMaxHeight ? this.expansionIncrementCount : this.sectionContentMaxHeight;
            //Math.max is used to eliminate any negative values
            this.decreaseExpanderContentMaxHeight(Math.max(0, decreaseByValue));

            this.clearExpansionInterval();

            this.expansionIntervalId = setInterval(() => {
                //If the content section's height reached 0 (the content section is collapsed)
                if (this.getSectionContentHeight() === 0) {
                    this.stopContentResizing();
                    return;
                }

                //Decrease the minimum height of the content
                const multipliedExpansionIncrementCount = this.expansionIncrementCount * expansionIncrementCountMultiplier;
                const decreaseByValue = multipliedExpansionIncrementCount < this.sectionContentMaxHeight ? multipliedExpansionIncrementCount : this.sectionContentMaxHeight;
                //Math.max is used to eliminate any negative values
                this.decreaseExpanderContentMaxHeight(Math.max(0, decreaseByValue));

                //Increase the height decrement multiplier
                expansionIncrementCountMultiplier += this.expansionMultiplierIncrement;
            }, this.expansionIncrementPace);
        }
        else {
            this.isSectionResizing = false;
            this.sectionExpanderContentRef.style.maxHeight = '0';
        }
    }

    /**
     * Get the content section's height
     */
    getSectionContentHeight = (): number => this.sectionExpanderContentRef.offsetHeight;

    /**
     * Stop resizing the content section (clear any ongoing setInterval(s))
     */
    stopContentResizing = () => {
        this.isSectionResizing = false;
        this.clearExpansionInterval();
    }

    /**
     * Toggle expander
     */
    public toggleExpander() {
        this.expanderCollapsed = !this.expanderCollapsed;

        this.resizeExpander();
        setTimeout(() => {
            const changeEvent = new CustomEvent("on-toggle", {
                detail: {
                    value: this.expanderCollapsed
                },
                bubbles: true
            });
            this.element.dispatchEvent(changeEvent);
        });
    }

    private resizeExpander() {
        //Cancel any existing resizing
        if (this.isSectionResizing) {
            this.stopContentResizing();
            this.isSectionResizing = false;
        }
        this.isSectionResizing = true;

        //The below methods are asynchronous (they trigger workloads using setIntervals)
        if (this.expanderCollapsed) {
            this.collapseSectionContentIncrementally();
        }
        else {
            this.expandSectionContentIncrementally();
        }
        //Any code placed below the method invocations is likely to be executed before the setInterval(s) within the methods return/finish
    }

    expanderCollapsedChanged() {
        if (this._isAttached)
            this.resizeExpander();
    }

    clearExpansionInterval() {
        if (this.expansionIntervalId) {
            clearInterval(this.expansionIntervalId);
            this.expansionIntervalId = null;
        }
    }
}